import React, { useState } from 'react';
import { MediaItem } from '../../../api/structs/media';
import { SceneGroup, getSceneThumb } from './MediaEditorSection.helpers';
import { RegenerateStatus } from './regenerateWatch';
import { readRegenerateFailure } from '../regenerateFailure';
import { formatSceneLabel } from '../sceneLabel';
import MediaThumbImage from '../MediaThumbImage';

interface SceneCardProps {
  scene: SceneGroup;
  index: number;
  /** Live regeneration of one of this scene's items, or null. Owned by the section. */
  regenerateStatus?: RegenerateStatus | null;
  isSelected: boolean;
  isDragTarget: boolean;
  onClick: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  /** SCENE-level: opens the Video | Frames regenerate modal, not an item regen. */
  onRegenerate: () => void;
  onReplace: (item: MediaItem) => void;
  onDelete: (item: MediaItem) => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onMoveLeft: () => void;
  onMoveRight: () => void;
}

const MediaEditorSceneCard: React.FC<SceneCardProps> = ({
  scene,
  index,
  regenerateStatus = null,
  isSelected,
  isDragTarget,
  onClick,
  onDragStart,
  onDragOver,
  onDrop,
  onRegenerate,
  onReplace,
  onDelete,
  canMoveLeft,
  canMoveRight,
  onMoveLeft,
  onMoveRight,
}) => {
  const [pendingDelete, setPendingDelete] = useState(false);
  const sceneThumb = getSceneThumb(scene);
  // VIDEO-first, mirroring MediaEditorSceneDetailPanel: the video is the
  // scene's deliverable, so hover Replace/Delete and the prompt preview target
  // it — not the opening-frame still.
  const primaryItem = scene.video || scene.image || scene.allItems[0];
  const sceneLabel = formatSceneLabel(scene.sceneId, index);
  const promptPreview = primaryItem?.prompt?.slice(0, 24) || '';
  // A FAILED regenerate leaves the scene's bytes untouched (that is the point),
  // so the only trace on the filmstrip is this marker. Without it the storyboard
  // re-renders byte-identical and the user is told nothing at all.
  const regenerateFailure = scene.allItems
    .map((entry) => readRegenerateFailure(entry))
    .find((failure) => failure !== null);

  return (
    <div
      className={`relative group w-32 shrink-0 rounded-lg border cursor-pointer transition-all snap-start ${
        isSelected
          ? 'border-white/50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]'
          : 'border-white/10 hover:border-white/25'
      } ${isDragTarget ? 'border-white/40 bg-white/5' : 'bg-black'}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onClick}
      onMouseLeave={() => setPendingDelete(false)}
    >
      <div className="w-full h-20 rounded-t-lg overflow-hidden bg-zinc-900">
        {sceneThumb.waiting ? (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-mono">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 animate-spin text-zinc-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </div>
        ) : sceneThumb.failed ? (
          // Terminal, not pending: this item's own generation failed and it is
          // parked on `_failed.png`. It used to spin the spinner above forever,
          // because one predicate answered both questions.
          <div
            data-testid="scene-thumb-failed"
            className="w-full h-full flex flex-col items-center justify-center gap-0.5 text-red-400/80 text-[10px] font-mono"
          >
            <span className="text-sm leading-none">⚠</span>
            <span>failed</span>
          </div>
        ) : (
          <MediaThumbImage
            thumbUrl={sceneThumb.thumbUrl}
            originalUrl={sceneThumb.originalUrl}
            videoUrl={sceneThumb.videoUrl}
            alt={promptPreview}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="px-2 py-1.5">
        <p className="text-[11px] text-zinc-300 font-mono truncate">{sceneLabel}</p>
        {promptPreview && (
          <p className="text-[10px] text-zinc-600 truncate">{promptPreview}…</p>
        )}
        {regenerateStatus === 'running' && (
          <p data-testid="scene-card-regenerating" className="text-[10px] text-amber-300/80 truncate">
            regenerating…
          </p>
        )}
        {regenerateStatus === 'stalled' && (
          <p data-testid="scene-card-regen-stalled" className="text-[10px] text-amber-400/80 truncate">
            no result yet
          </p>
        )}
        {!regenerateStatus && regenerateFailure && (
          <p data-testid="scene-card-regen-failed" className="text-[10px] text-red-400/80 truncate">
            regenerate failed
          </p>
        )}
        <div className="flex gap-1 mt-0.5">
          {scene.video && <span className="text-[10px] text-zinc-500">▶</span>}
        </div>
      </div>

      {primaryItem && (
        <div className="absolute inset-0 rounded-lg bg-black/80 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          {canMoveLeft && (
            <button
              type="button"
              title="Move left"
              className="text-zinc-300 text-base hover:text-white transition-colors font-mono"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveLeft(); }}
            >←</button>
          )}
          {canMoveRight && (
            <button
              type="button"
              title="Move right"
              className="text-zinc-300 text-base hover:text-white transition-colors font-mono"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveRight(); }}
            >→</button>
          )}
          <button
            type="button"
            title="Regenerate"
            className="text-zinc-300 text-base hover:text-white transition-colors font-mono"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRegenerate(); }}
          >↻</button>
          <button
            type="button"
            title="Replace"
            className="text-zinc-300 text-base hover:text-white transition-colors font-mono"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onReplace(primaryItem!); }}
          >⇄</button>
          {!pendingDelete ? (
            <button
              type="button"
              title="Delete"
              className="text-zinc-300 text-base hover:text-white transition-colors font-mono"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPendingDelete(true); }}
            >✕</button>
          ) : (
            <>
              <span className="text-zinc-400 text-[10px] font-mono">sure?</span>
              <button
                type="button"
                className="text-green-400 text-base hover:text-green-200 font-mono"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(primaryItem!); setPendingDelete(false); }}
              >✓</button>
              <button
                type="button"
                className="text-zinc-400 text-base hover:text-zinc-200 font-mono"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPendingDelete(false); }}
              >✕</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MediaEditorSceneCard;
